import { useEffect } from "react";
import { fetchBranding } from "../api/client";
import { useAsyncData } from "./useAsyncData";
import type { Branding } from "../api/types";

const FALLBACK_TITLE = "Admin Console";

/**
 * Loads runtime branding from GET /api/branding and applies it to the page: the theme colour as a
 * CSS custom property (so every component that reads `var(--brand-theme-color)` re-themes for
 * free), plus the document title and favicon. This is the only place branding touches the DOM
 * outside of components that render it directly (the logo).
 */
export function useBranding(): {
  branding: Branding | null;
  loading: boolean;
  error: Error | null;
} {
  const { data: branding, loading, error } = useAsyncData(() => fetchBranding(), []);

  useEffect(() => {
    if (!branding) {
      return;
    }
    document.documentElement.style.setProperty("--brand-theme-color", branding.theme_color);
    document.title = branding.name || FALLBACK_TITLE;

    const link = document.getElementById("favicon-link");
    if (link instanceof HTMLLinkElement && branding.favicon) {
      link.href = branding.favicon;
    }
  }, [branding]);

  return { branding, loading, error };
}
